import React, { useState } from 'react';
import { getCurrentUsername } from '../utils/userManager';
import './GameMenu.css';

const GameMenu = ({ onAction, botDifficulty }) => {
  const [showPlayerNames, setShowPlayerNames] = useState(false);
  const [showDifficultySelect, setShowDifficultySelect] = useState(false);
  const [playerNames, setPlayerNames] = useState(() => {
    const currentUsername = getCurrentUsername();
    return { 
      red: currentUsername || 'Red Player', 
      yellow: 'Yellow Player' 
    };
  });

  const handlePlayVsPlayer = () => {
    setShowPlayerNames(true);
  };

  const handlePlayVsComputer = () => {
    setShowDifficultySelect(true);
  };

  const handleStartPvP = () => {
    onAction('playVsPlayer', { names: playerNames });
    setShowPlayerNames(false);
  };

  const handleStartPvC = (difficulty) => {
    onAction('playVsComputer', { difficulty });
    setShowDifficultySelect(false);
  };

  if (showPlayerNames) {
    return (
      <div className="game-menu">
        <div className="menu-container">
          <div className="game-title-large">
            <h1>CONNECT 4</h1>
            <div className="title-underline"></div>
          </div>
          
          <div className="setup-table">
            <div className="table-header">
              <h2>Player Setup</h2>
            </div>
            
            <div className="setup-form">
              <div className="input-row">
                <div className="player-input red-player">
                  <label>Red Player Name</label>
                  <input 
                    type="text" 
                    value={playerNames.red}
                    onChange={(e) => setPlayerNames(prev => ({ ...prev, red: e.target.value }))}
                    placeholder="Enter red player name"
                  />
                  <div className="input-indicator red"></div>
                </div>
              </div>
              
              <div className="vs-divider">
                <span>VS</span>
              </div>
              
              <div className="input-row">
                <div className="player-input yellow-player">
                  <label>Yellow Player Name</label>
                  <input 
                    type="text" 
                    value={playerNames.yellow}
                    onChange={(e) => setPlayerNames(prev => ({ ...prev, yellow: e.target.value }))}
                    placeholder="Enter yellow player name"
                  />
                  <div className="input-indicator yellow"></div>
                </div>
              </div>
            </div>
            
            <div className="setup-actions">
              <button className="action-btn primary" onClick={handleStartPvP}>
                Start Game
              </button>
              <button className="action-btn secondary" onClick={() => setShowPlayerNames(false)}>
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showDifficultySelect) {
    return (
      <div className="game-menu">
        <div className="menu-container">
          <div className="game-title-large">
            <h1>CONNECT 4</h1>
            <div className="title-underline"></div>
          </div>
          
          <div className="difficulty-table">
            <div className="table-header">
              <h2>Choose Your AI Opponent</h2>
              <div className="difficulty-subtitle">Select the challenge level that suits you best</div>
            </div>
            
            <div className="difficulty-grid">
              <div className="difficulty-row">
                <button 
                  className={`difficulty-option easy ${botDifficulty === 3 ? 'selected' : ''}`}
                  onClick={() => handleStartPvC(3)}
                >
                  <div className="difficulty-icon">🤖</div>
                  <div className="difficulty-content">
                    <div className="difficulty-title">ROOKIE AI</div>
                    <div className="difficulty-subtitle">Perfect for beginners</div>
                    <div className="difficulty-details">Relaxed gameplay, makes occasional mistakes</div>
                  </div>
                  <div className="difficulty-indicator"></div>
                </button>
              </div>
              
              <div className="difficulty-row">
                <button 
                  className={`difficulty-option medium ${botDifficulty === 6 ? 'selected' : ''}`}
                  onClick={() => handleStartPvC(6)}
                >
                  <div className="difficulty-icon">⚔️</div>
                  <div className="difficulty-content">
                    <div className="difficulty-title">WARRIOR AI</div>
                    <div className="difficulty-subtitle">Balanced challenge</div>
                    <div className="difficulty-details">Strategic thinking, good defense and offense</div>
                  </div>
                  <div className="difficulty-indicator"></div>
                </button>
              </div>
              
              <div className="difficulty-row">
                <button 
                  className={`difficulty-option hard ${botDifficulty === 9 ? 'selected' : ''}`}
                  onClick={() => handleStartPvC(9)}
                >
                  <div className="difficulty-icon">👑</div>
                  <div className="difficulty-content">
                    <div className="difficulty-title">MASTER AI</div>
                    <div className="difficulty-subtitle">Ultimate challenge</div>
                    <div className="difficulty-details">Nearly unbeatable, calculates every move</div>
                  </div>
                  <div className="difficulty-indicator"></div>
                </button>
              </div>
            </div>
            
            <div className="setup-actions">
              <button className="action-btn secondary" onClick={() => setShowDifficultySelect(false)}>
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-menu">
      <div className="menu-container">
        {/* Large Centered Game Title */}
        <div className="game-title-large">
          <h1>CONNECT 4</h1>
          <div className="title-underline"></div>
          <div className="floating-pieces">
            <div className="floating-piece red piece-1"></div>
            <div className="floating-piece yellow piece-2"></div>
            <div className="floating-piece red piece-3"></div>
            <div className="floating-piece yellow piece-4"></div>
            <div className="floating-piece red piece-5"></div>
            <div className="floating-piece yellow piece-6"></div>
          </div>
        </div>
        
        {/* Centered Options Table */}
        <div className="game-options-table">
          <div className="table-header">
            <h2>Game Options</h2>
          </div>
          
          <div className="options-grid">
            <div className="option-row">
              <button 
                className="option-btn primary pvp-option"
                onClick={handlePlayVsPlayer}
              >
                <div className="option-content">
                  <div className="option-title">Player vs Player</div>
                  <div className="option-subtitle">Challenge a friend locally</div>
                </div>
                <div className="option-indicator"></div>
              </button>
            </div>
            
            <div className="option-row">
              <button 
                className="option-btn primary pvc-option"
                onClick={handlePlayVsComputer}
              >
                <div className="option-content">
                  <div className="option-title">Player vs Computer</div>
                  <div className="option-subtitle">Test your skills against AI</div>
                </div>
                <div className="option-indicator"></div>
              </button>
            </div>
            
            <div className="option-row">
              <button 
                className="option-btn primary online-option"
                onClick={() => onAction('onlineMultiplayer')}
              >
                <div className="option-content">
                  <div className="option-title">Online Multiplayer</div>
                  <div className="option-subtitle">Play with friends online</div>
                </div>
                <div className="option-indicator"></div>
              </button>
            </div>
            
            <div className="option-row">
              <button 
                className="option-btn secondary stats-option"
                onClick={() => onAction('showStats')}
              >
                <div className="option-content">
                  <div className="option-title">Statistics</div>
                  <div className="option-subtitle">View your game history</div>
                </div>
                <div className="option-indicator"></div>
              </button>
            </div>
            
            <div className="option-row">
              <button 
                className="option-btn secondary help-option"
                onClick={() => onAction('help')}
              >
                <div className="option-content">
                  <div className="option-title">Help & Rules</div>
                  <div className="option-subtitle">Learn how to play</div>
                </div>
                <div className="option-indicator"></div>
              </button>
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="decorative-circles">
          <div className="circle circle-1"></div>
          <div className="circle circle-2"></div>
          <div className="circle circle-3"></div>
          <div className="circle circle-4"></div>
        </div>
      </div>
    </div>
  );
};

export default GameMenu;
